/**
 * The per-repository "Agents use this repository's skills and rules" switch,
 * in the Team edit sheet's "Works on" list (YAG-784; on by default since
 * YAG-1006). A Team owner who is not a workspace admin never sees the
 * Repositories tab of Connections, so this is where they switch it off, or
 * back on, for the repositories their Team works on.
 *
 * One small row per repository already attached. The sheet itself opens only
 * for people who may manage the Team; a row shows only when the server also
 * says this person may change that repository (`canEdit`). The switch acts at
 * once, like Pause, and goes back if the write is refused.
 */
import { useEffect, useState } from "react";
import { Switch } from "../ui/switch.js";
import { AGENT_TRUST_COPY, AGENT_TRUST_TITLE } from "../lib/agentSourcesCopy.js";
import { useRepoAgentTrust } from "../settings/useRepoAgentTrust.js";
import { fetchWorkspaceRepos } from "../lib/workspaceRepos.js";

/** The workspace's repositories by `owner/name` (lower-cased) → id. */
function useWorkspaceRepoIds(enabled: boolean): Map<string, string> {
  const [ids, setIds] = useState<Map<string, string>>(() => new Map());
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void (async () => {
      try {
        const next = new Map<string, string>();
        for (const repo of await fetchWorkspaceRepos())
          next.set(repo.fullName.toLowerCase(), repo.id);
        if (live) setIds(next);
      } catch (err) {
        // The switches are a convenience (Settings, Agent runs has them too), so
        // the section goes quiet, with one line to say why.
        console.warn(
          "Team settings: the repositories read failed; the skills switches stay off the list.",
          err,
        );
      }
    })();
    return () => {
      live = false;
    };
  }, [enabled]);
  return ids;
}

function TrustRow({
  repoId,
  fullName,
  disabled,
}: {
  repoId: string;
  fullName: string;
  disabled: boolean;
}) {
  const { trust, busy, error, setTrusted } = useRepoAgentTrust(repoId);
  if (!trust?.canEdit) return null;
  return (
    <li className="flex items-center justify-between gap-3" data-testid="team-edit-repo-trust">
      <span className="tm-field-note min-w-0">
        <span className="font-mono">{fullName}</span> · {AGENT_TRUST_TITLE}
        {error && (
          <span role="alert" className="block">
            {error}
          </span>
        )}
      </span>
      <Switch
        aria-label={`${AGENT_TRUST_TITLE}: ${fullName}`}
        title={AGENT_TRUST_COPY}
        checked={trust.trusted}
        disabled={disabled || busy}
        onCheckedChange={(next) => void setTrusted(next)}
      />
    </li>
  );
}

export function TeamRepoAgentTrust({
  repos,
  disabled,
}: {
  repos: readonly string[];
  disabled: boolean;
}) {
  const ids = useWorkspaceRepoIds(repos.length > 0);
  const rows = repos.flatMap((fullName) => {
    const id = ids.get(fullName.toLowerCase());
    return id ? [{ id, fullName }] : [];
  });
  if (rows.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-1.5 p-0" data-testid="team-edit-repo-trusts">
      {rows.map((row) => (
        <TrustRow key={row.id} repoId={row.id} fullName={row.fullName} disabled={disabled} />
      ))}
    </ul>
  );
}
